import { z } from 'zod';
import type { PerfilPadrao } from '../enums/dominios';

// Contratos de acesso entre front e API (RQ-ACE-001, RQ-ACE-010, RQ-ACE-011). Só uuids trafegam.

export const esquemaEntrar = z.object({
  email: z.string().trim().min(3).max(254),
  senha: z.string().min(1).max(200),
  /** Token do reCAPTCHA v3 (ação `login`, RQ-ACE-017). */
  recaptcha: z.string().max(4000).optional(),
});

export type EntrarEntrada = z.infer<typeof esquemaEntrar>;

/** JWT de acesso, guardado só na memória da aba. O token de renovação vai em cookie httpOnly. */
export type TokenAcessoDto = { tokenAcesso: string; expiraEmSegundos: number };

export type QuadroEuDto = { uuid: string; nome: string };
/** `minutosInatividade`: limite da empresa para o logout por inatividade (RQ-ACE-014). */
export type EmpresaEuDto = { uuid: string; nome: string; administrador: boolean; minutosInatividade: number; quadros: QuadroEuDto[] };

/** Quem está conectado e os espaços de trabalho (empresas ativas e quadros onde tem perfil). */
export type EuDto = {
  usuario: { uuid: string; nome: string; email: string; avatarUrl: string | null };
  empresas: EmpresaEuDto[];
};

// Convites (RQ-ACE-003): o administrador do quadro convida por e-mail com um perfil; a pessoa aceita criando a senha
// ou entrando com o Google da mesma conta. Quem já tem conta é adicionado na hora.

export const esquemaConvidar = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  perfil: z.number().int().min(1).max(4),
  /** Gestor direto de quem é convidado como colaborador ou leitor (só o administrador escolhe). */
  gestorUuid: z.string().uuid().optional(),
});
export type ConvidarEntrada = z.infer<typeof esquemaConvidar>;
/**
 * `adicionado`: a pessoa já tinha conta e entrou no quadro na hora (recebe só um aviso por e-mail).
 * `aguardando`: convite de colaborador, esperando a aprovação do gestor direto (ADR-0021).
 */
export type ResultadoConviteDto = { situacao: 'convidado' | 'adicionado' | 'aguardando' };

/** O que a pessoa pode fazer no formulário de convite: perfis, gestores para escolher e se passa por aprovação. */
export type OpcoesConviteDto = { perfis: PerfilPadrao[]; gestores: { uuid: string; nome: string }[]; exigeAprovacao: boolean };

/** Convite esperando a aprovação do gestor direto (tela aberta pelo aviso no sino). */
export type ConviteParaAprovarDto = {
  uuid: string;
  email: string;
  perfil: string;
  convidadoPor: string;
  gestor: string | null;
  criadoEm: string;
  /** Até quando pode ser aprovado (7 dias). */
  aprovarAte: string;
  situacao: 'aguardando' | 'aprovado' | 'recusado' | 'vencido';
  motivoRecusa: string | null;
  podeDecidir: boolean;
};

export const esquemaRecusarConvite = z.object({ motivo: z.string().trim().max(500).optional() });

// Membros do quadro (RQ-ACE-018): quem está no quadro, o gestor direto, o papel e os convites pendentes.

export type MembroDoQuadroDto = {
  /** Uuid do usuário. */
  uuid: string;
  nome: string;
  email: string;
  avatarUrl: string | null;
  perfil: string;
  perfilPadrao: PerfilPadrao | null;
  gestor: { uuid: string; nome: string } | null;
  voce: boolean;
  /** Colaborador ou leitor, e quem vê é o administrador ou o gestor atual dele. */
  podeTrocarGestor: boolean;
};

/**
 * `aguardando-aprovacao`: convite de colaborador que o gestor ainda não aprovou (o e-mail não saiu).
 * `vencido`: passou do prazo; não se reenvia, envia-se um convite novo.
 */
export type ConvitePendenteDto = {
  uuid: string;
  email: string;
  perfil: string;
  convidadoPor: string;
  gestor: string | null;
  criadoEm: string;
  expiraEm: string;
  situacao: 'aguardando-aprovacao' | 'enviado' | 'vencido';
  reenvios: number;
  podeReenviar: boolean;
  podeCancelar: boolean;
  podeConvidarDeNovo: boolean;
};

export type MembrosDoQuadroDto = {
  quadro: string;
  membros: MembroDoQuadroDto[];
  convites: ConvitePendenteDto[];
  /** Gestores do quadro (para trocar o gestor direto ou escolher um ao mudar o papel). */
  gestores: { uuid: string; nome: string }[];
  podeMudarPapel: boolean;
  podeRemover: boolean;
  limiteReenvios: number;
};

export const esquemaTrocarGestor = z.object({ gestorUuid: z.string().uuid() });
/** Novo papel; para colaborador e leitor, o gestor direto (sem ele, o administrador que mudou). */
export const esquemaMudarPapel = z.object({ perfil: z.number().int().min(1).max(4), gestorUuid: z.string().uuid().optional() });
export type MudarPapelEntrada = z.infer<typeof esquemaMudarPapel>;

export type ConviteDto = {
  email: string;
  empresa: string;
  quadro: string;
  perfil: string;
  convidadoPor: string;
  expiraEm: string;
  situacao: 'valido' | 'expirado' | 'aceito';
};

export const esquemaAceitarConvite = z.object({
  nome: z.string().trim().min(2).max(200),
  senha: z.string().min(8).max(200),
});
export type AceitarConviteEntrada = z.infer<typeof esquemaAceitarConvite>;

// Acesso mediante convite: quem tenta entrar com o Google sem conta nem convite pede acesso por um formulário.

export const esquemaSolicitarAcesso = z.object({
  /** Vem do retorno do Google: e-mail e nome confirmados por ele (30 minutos). */
  token: z.string().max(4000).optional(),
  nome: z.string().trim().min(2).max(200),
  email: z.string().trim().toLowerCase().email().max(254).optional(),
  telefone: z.string().trim().min(8).max(40),
  empresa: z.string().trim().min(2).max(200),
});
export type SolicitarAcessoEntrada = z.infer<typeof esquemaSolicitarAcesso>;
/** Dados do Google para preencher o formulário. */
export type SolicitacaoPreenchidaDto = { email: string; nome: string | null };
